const API_BASE = process.env.REACT_APP_API_URL ?? '';

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  skipAuth = false
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const incomingHeaders = ((options.headers as Record<string, string>) ?? {});
  const body = options.body;
  const isFormDataBody = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers: Record<string, string> = {
    'X-Service': 'bmcs',
    ...incomingHeaders,
  };
  if (!isFormDataBody && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (!skipAuth && accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data?.error ?? res.statusText);
  return data as T;
}

/** Use for public (guest) endpoints that must not send auth (e.g. public event RSVP). */
export function requestWithoutAuth<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  return request<T>(path, options, true);
}

/** API client that never sends Authorization – for public/guest endpoints only. */
export const apiPublic = {
  get: <T>(path: string) => requestWithoutAuth<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    requestWithoutAuth<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  postForm: <T>(path: string, body: FormData) =>
    request<T>(path, { method: 'POST', body }),
  delete: (path: string) => request<void>(path, { method: 'DELETE' }),
};
